import { jsPDF } from "jspdf";
import { LOGO_URL, CERT_LOGOS, DISCLAIMER_TITLE, DISCLAIMER_PARAGRAPHS } from "./api";

async function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

const money = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export async function generateQuotePDF({ customer = {}, address, estimate }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const red = [239, 35, 60];

  // Header band
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, W, 90, "F");

  const logo = await loadImage(LOGO_URL);
  if (logo) {
    const h = 64;
    const w = (logo.w / logo.h) * h;
    doc.addImage(logo.dataUrl, "PNG", 36, 13, w, h);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("ROOFING ESTIMATE", W - 36, 40, { align: "right" });
  doc.setFontSize(10);
  doc.setTextColor(180, 180, 180);
  doc.text("Roof Fix Houston  •  Houston, TX", W - 36, 58, { align: "right" });
  doc.text(new Date().toLocaleDateString("en-US", { dateStyle: "long" }), W - 36, 72, { align: "right" });

  let y = 125;
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("PREPARED FOR", 36, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y += 18;
  if (customer.name) doc.text(customer.name, 36, y), (y += 15);
  if (customer.email) doc.text(customer.email, 36, y), (y += 15);
  if (customer.phone) doc.text(customer.phone, 36, y), (y += 15);
  if (address) {
    doc.setFont("helvetica", "bold");
    doc.text("PROPERTY", W / 2, 125);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(address, W / 2 - 50);
    doc.text(lines, W / 2, 143);
  }

  y = Math.max(y, 200);

  // Roof summary
  doc.setDrawColor(...red);
  doc.setLineWidth(2);
  doc.line(36, y, W - 36, y);
  y += 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const propLabel = estimate.property_type === "commercial" ? "Commercial" : "Residential";
  doc.text(`${propLabel}  —  ${estimate.material_name}  —  ${estimate.pitch_label}`, 36, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text(
    `Roof Area: ${estimate.roof_area_sqft.toLocaleString()} sq ft   •   ${estimate.squares} squares   •   Waste: ${estimate.waste_pct}%`,
    36,
    y
  );
  y += 28;

  // Line items — overhead & profit are folded into labor; not shown separately
  const li = estimate.line_items;
  const laborWithMargin = (li.labor_cost || 0) + (li.overhead || 0) + (li.profit || 0);
  const rows = [
    ["Materials (incl. waste)", li.material_cost],
    ["Labor & Installation", laborWithMargin],
    ["Tear-off & Removal", li.tearoff_cost],
    ["Disposal / Dumpster", li.disposal_cost],
    ["Permit & Inspection", li.permit_cost],
  ];
  if (li.multi_story_markup > 0) rows.push(["Multi-Story Adjustment (15%)", li.multi_story_markup]);

  doc.setTextColor(20, 20, 20);
  rows.forEach(([label, val]) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(label, 40, y);
    doc.text(money(val), W - 40, y, { align: "right" });
    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(0.5);
    doc.line(36, y + 7, W - 36, y + 7);
    y += 24;
  });

  // Total
  y += 8;
  doc.setFillColor(10, 10, 10);
  doc.rect(36, y - 14, W - 72, 40, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("TOTAL ESTIMATE", 48, y + 11);
  doc.setTextColor(...red);
  doc.setFontSize(18);
  doc.text(money(estimate.total), W - 48, y + 12, { align: "right" });

  y += 34;
  const pageH = doc.internal.pageSize.getHeight();
  const ensureSpace = (needed) => {
    if (y + needed > pageH - 40) {
      doc.addPage();
      y = 50;
    }
  };

  // Full disclaimer (below estimate)
  ensureSpace(60);
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(DISCLAIMER_TITLE, 36, y);
  y += 15;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  DISCLAIMER_PARAGRAPHS.forEach((p) => {
    const lines = doc.splitTextToSize(p, W - 72);
    ensureSpace(lines.length * 10 + 8);
    doc.text(lines, 36, y);
    y += lines.length * 10 + 8;
  });

  // Manufacturer certifications strip
  const certImgs = await Promise.all(CERT_LOGOS.map((c) => loadImage(c.url)));
  const badgeH = 30;
  const gap = 14;
  const widths = certImgs.map((im) => (im ? (im.w / im.h) * badgeH : badgeH));
  const totalW = widths.reduce((a, b) => a + b, 0) + gap * (certImgs.length - 1);
  ensureSpace(badgeH + 30);
  y += 14;
  doc.setTextColor(120, 120, 120);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("FACTORY-CERTIFIED & TRUSTED", W / 2, y, { align: "center" });
  y += 8;
  let bx = (W - totalW) / 2;
  certImgs.forEach((im, i) => {
    if (im) doc.addImage(im.dataUrl, "PNG", bx, y, widths[i], badgeH);
    bx += widths[i] + gap;
  });

  doc.save(`RoofFix-Estimate-${(customer.name || "quote").replace(/\s+/g, "-")}.pdf`);
}
